/* @jsx h */
import { defineComponent, h, ref, inject, onMounted, onUnmounted } from "vue";
import { RouterLink } from "vue-router";
import {
  OrdersBreakdown,
  OrdersStatusReportDatum,
  PaymentsSummaryReport,
} from "@lemmonhq/types";
import { formatPriceInLocale } from "@lemmonhq/orders";
import { useLocalization } from "@lemmonhq/uikit";
import type { Ref } from "vue";

type MobileSummaryCardProps = {
  data: PaymentsSummaryReport;
  ordersBreakdown: OrdersBreakdown;
  currency: string;
  showExportLink?: boolean;
};

type MetricProps = {
  label: string;
  icon: string;
  iconClass: string;
  value: string | number;
  description: string;
};

const Metric = defineComponent<MetricProps>({
  name: "MobileSummaryMetric",
  props: ["label", "icon", "iconClass", "value", "description"],
  setup(props) {
    return () => (
      <div class="grid h-full min-w-0 grid-cols-[minmax(0,1fr)_minmax(3.5rem,auto)] items-stretch gap-1 px-2 py-2">
        <div class="flex min-w-0 flex-col justify-center">
          <div class="flex min-w-0 items-center gap-1 text-base text-base-content/80">
            <span
              class={[
                "material-symbols-outlined flex size-8 shrink-0 items-center justify-center rounded-full bg-base-200 p-0 text-2xl leading-none",
                props.iconClass,
              ]}
            >
              {props.icon}
            </span>
            <span class="truncate text-accent/80">{props.label}</span>
          </div>
          <div
            title={props.description}
            class="w-full min-w-0 truncate whitespace-nowrap text-[10px] leading-3.5 text-base-content/50"
          >
            {props.description}
          </div>
        </div>
        <div class="flex min-w-0 items-center justify-center text-center text-1xl tracking-tight text-accent p-1 px-2 w-24">
          <span class="max-w-full truncate">{props.value}</span>
        </div>
      </div>
    );
  },
});

export const MobileSummaryCard = defineComponent<MobileSummaryCardProps>({
  name: "MobileSummaryCard",
  props: ["data", "ordersBreakdown", "currency", "showExportLink"],
  setup(props) {
    const { t } = useLocalization();
    const revenueRef = ref<HTMLElement | null>(null);
    const scrollProgress = inject<Ref<number>>("mobileRevenueScrollProgress");
    let scrollEl: Element | null = null;

    const updateProgress = () => {
      if (!revenueRef.value || !scrollProgress) return;
      const rect = revenueRef.value.getBoundingClientRect();
      const headerH = 56; // MobileOverviewHeader height
      // Progress goes 0→1 as the revenue element scrolls from visible to fully above the header
      const p = Math.max(0, Math.min(1, (headerH - rect.top) / rect.height));
      scrollProgress.value = p;
    };

    onMounted(() => {
      let el = revenueRef.value?.parentElement ?? null;
      while (el) {
        const { overflowY } = getComputedStyle(el);
        if (overflowY === "auto" || overflowY === "scroll") {
          scrollEl = el;
          break;
        }
        el = el.parentElement;
      }
      const target = scrollEl ?? window;
      updateProgress();
      target.addEventListener("scroll", updateProgress, { passive: true });
    });

    onUnmounted(() => {
      const target = scrollEl ?? window;
      target.removeEventListener("scroll", updateProgress);
    });

    return () => {
      const byService = props.ordersBreakdown.by_service_method || [];
      const byStatus = props.ordersBreakdown.by_status || [];
      const totalCount = byService.reduce(
        (sum, item) => sum + item.total_count || 0,
        0,
      );
      const totalRevenue = byService.reduce(
        (sum, item) => sum + item.total_revenue || 0,
        0,
      );
      const currency = props.currency;
      const canceledOrders: OrdersStatusReportDatum = byStatus.find(
        (item) => item.status === "canceled",
      ) || {
        status: "canceled",
        total_revenue: 0,
        total_count: 0,
      };

      return (
        <section class="-mx-2 -mt-4 w-[calc(100%+1rem)] overflow-hidden md:hidden">
          <div class="relative isolate overflow-hidden px-4 pb-3 pt-2 text-center">
            {props.showExportLink !== false ? (
              // Was a bare 10px text link, so its hit area measured 20px tall
              // against the 32px minimum — on the mobile card that is the
              // primary device this is exactly where it matters most.
              <RouterLink
                to="/app/reports/export"
                class="absolute right-1 top-0 flex h-8 items-center gap-0.5 rounded-lg px-2 text-[11px] font-medium text-accent"
              >
                {t("reports.nav.export")}
                <span class="material-symbols-outlined text-sm">
                  file_export
                </span>
              </RouterLink>
            ) : null}
            <div
              ref={revenueRef}
              class="text-[clamp(2.4rem,12vw,3.25rem)] font-semibold tracking-[-0.04em] text-accent"
            >
              {formatPriceInLocale(props.data.total_amount, true, 0, currency)}
            </div>
            <div class="mt-0 inline-flex items-center gap-1 rounded-full bg-base-200/80 px-2.5 py-0.5 text-xs font-medium text-base-content/70 shadow-sm backdrop-blur-sm">
              <span class="material-symbols-outlined text-sm text-success">
                trending_up
              </span>
              {props.data.total_count} {t("reports.dashboard.payments")}
            </div>
          </div>

          <div class="flex flex-col bg-base-100">
            <Metric
              label={t("reports.dashboard.tips")}
              icon="savings"
              iconClass="text-success"
              value={formatPriceInLocale(
                props.data.total_tips,
                false,
                2,
                currency,
              )}
              description={`${t("reports.dashboard.avg")} ${currency} ${formatPriceInLocale(props.data.total_tips / totalCount || 0, false, 2, currency)}/${t("reports.dashboard.order")}`}
            />
            <Metric
              label={t("reports.dashboard.discount")}
              icon="monitoring"
              iconClass="text-warning"
              value={formatPriceInLocale(
                props.data.discount_amount || 0,
                false,
                2,
                currency,
              )}
              description={`${props.data.discount_count} ${t("reports.dashboard.orders")}`}
            />
            <Metric
              label={t("reports.dashboard.cancelled")}
              icon="delete"
              iconClass="text-error"
              value={formatPriceInLocale(
                canceledOrders.total_revenue,
                false,
                2,
                currency,
              )}
              description={`${canceledOrders.total_count} ${t("reports.dashboard.orders")}`}
            />
            <Metric
              label={t("reports.dashboard.orders")}
              icon="receipt_long"
              iconClass="text-info"
              value={totalCount}
              description={`${formatPriceInLocale(totalRevenue / totalCount || 0, true, 2, currency)} ${t("reports.dashboard.avg")}`}
            />
          </div>
        </section>
      );
    };
  },
});
